import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SignatureMaker } from '@docuseal/signature-maker-react';
import { FormService } from '../../services/FormService';
import { getFormConfig } from '../index';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import schema from './schema';
import validator from './validator';
import defaultData from './defaultData';
import './styles/DomesticGasSafetyForm.module.css';

const { FiPlus, FiTrash2, FiSave, FiFileText, FiAlertCircle } = FiIcons;

const DomesticGasSafetyForm = ({ formInstanceId, onSave, onGeneratePDF }) => {
  const navigate = useNavigate();
  const formConfig = getFormConfig('domestic-gas-safety');
  
  const [formData, setFormData] = useState(defaultData);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (formInstanceId) {
      loadFormData(formInstanceId);
    }
  }, [formInstanceId]);

  const loadFormData = async (instanceId) => {
    setLoading(true);
    try {
      const form = await FormService.getForm(instanceId);
      if (form && form.data) {
        setFormData(form.data);
      }
    } catch (error) {
      console.error('Error loading form data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (section, field, value, index = null) => {
    setFormData(prev => {
      const newData = { ...prev };
      
      if (index !== null) {
        if (!newData[section]) newData[section] = [];
        if (!newData[section][index]) newData[section][index] = {};
        newData[section][index][field] = value;
      } else {
        if (!newData[section]) newData[section] = {};
        newData[section][field] = value;
      }
      
      return newData;
    });

    if (errors[field] || errors[`${section}_${field}`]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        delete newErrors[`${section}_${field}`];
        return newErrors;
      });
    }
  };

  const handleSignatureChange = (section, field, event) => {
    console.log('Signature change event:', event);
    
    if (event && event.base64) {
      let signatureData = event.base64;
      if (!signatureData.startsWith('data:image')) {
        signatureData = `data:image/png;base64,${signatureData}`;
      }
      handleFieldChange(section, field, signatureData);
    } else {
      console.warn('No signature data in event');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const validation = validator.validate(formData);
      if (!validation.isValid) {
        setErrors(validation.errors);
        setSaving(false);
        return;
      }

      const savedForm = await FormService.saveForm(formData, 'domestic-gas-safety', formInstanceId);
      
      if (onSave) onSave(savedForm);
      
      if (!formInstanceId && savedForm.id) {
        navigate(`/forms/domestic-gas-safety/${savedForm.id}`, { replace: true });
      }
    } catch (error) {
      console.error('Error saving form:', error);
      alert('Failed to save form. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleGeneratePDF = () => {
    const validation = validator.validate(formData);
    
    if (!validation.isValid) {
      setErrors(validation.errors);
      alert('Please fix all validation errors before generating PDF');
      return;
    }

    setErrors({});
    
    if (onGeneratePDF) {
      onGeneratePDF(formData);
    } else {
      console.error('onGeneratePDF handler not provided');
      alert('PDF generation is not available');
    }
  };

  const renderField = (field, sectionId, value, onChange, index = null) => {
    const fieldId = index !== null ? `${sectionId}_${field.id}_${index}` : `${sectionId}_${field.id}`;
    const error = errors[fieldId] || errors[field.id];

    if (field.type === 'signature') {
      return (
        <div key={fieldId} className="form-group col-span-2">
          <label htmlFor={fieldId} className="form-label">
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <SignatureMaker
            withSubmit={false}
            onChange={(event) => handleSignatureChange(sectionId, field.id, event)}
          />
          {value && (
            <div className="mt-4">
              <p className="text-sm text-gray-600 mb-2">Saved signature:</p>
              <div className="border border-gray-300 rounded p-2 bg-white">
                {value.startsWith('data:image') ? (
                  <img 
                    src={value} 
                    alt="Signature" 
                    style={{ maxWidth: '400px', maxHeight: '150px', objectFit: 'contain' }}
                  />
                ) : (
                  <p className="text-red-500 text-sm">Invalid signature format</p>
                )}
              </div>
            </div>
          )}
          {error && <span className="text-red-500 text-sm mt-1 block">{error}</span>}
        </div>
      );
    }

    switch (field.type) {
      case 'text':
      case 'tel':
      case 'date':
      case 'number':
        return (
          <div key={fieldId} className="form-group">
            <label htmlFor={fieldId} className="form-label">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              type={field.type}
              id={fieldId}
              className={`form-input ${error ? 'border-red-500' : ''}`}
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
              placeholder={field.placeholder}
            />
            {error && <span className="text-red-500 text-sm mt-1">{error}</span>}
          </div>
        );

      case 'textarea':
        return (
          <div key={fieldId} className="form-group col-span-2">
            <label htmlFor={fieldId} className="form-label">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <textarea
              id={fieldId}
              className={`form-input ${error ? 'border-red-500' : ''}`}
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
              rows={3}
              placeholder={field.placeholder}
            />
            {error && <span className="text-red-500 text-sm mt-1">{error}</span>}
          </div>
        );

      case 'select':
        return (
          <div key={fieldId} className="form-group">
            <label htmlFor={fieldId} className="form-label">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <select
              id={fieldId}
              className={`form-input ${error ? 'border-red-500' : ''}`}
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
            >
              <option value="">Select {field.label}</option>
              {field.options?.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
            {error && <span className="text-red-500 text-sm mt-1">{error}</span>}
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Loading form...</p>
        </div>
      </div>
    );
  }

  const hasErrors = Object.keys(errors).length > 0;

  return (
    <div className="domesticGasSafety-form max-w-7xl mx-auto p-6">
      <div className="form-header mb-6 bg-white p-6 rounded-lg shadow">
        <h1 className="text-3xl font-bold text-gray-900 mb-2 text-center">
          Domestic Gas Safety Record
        </h1>
        <p className="text-gray-600 text-center">
          {formConfig?.description || 'Gas Safety Certificate'}
        </p>
      </div>

      {hasErrors && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start">
          <SafeIcon icon={FiAlertCircle} className="w-5 h-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-red-800 mb-1">Please fix the following errors:</h3>
            <ul className="text-sm text-red-700 list-disc list-inside">
              {Object.entries(errors).map(([field, error]) => (
                <li key={field}>{error}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <form onSubmit={(e) => e.preventDefault()}>
        {schema.sections.map(section => (
          <div key={section.id} className="card mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 border-b border-gray-200 pb-2">
              {section.title}
            </h2>
            
            {section.repeatable ? (
              <div>
                {formData[section.id]?.map((item, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4 mb-4 relative bg-gray-50">
                    <h3 className="font-medium text-gray-800 mb-4 text-lg">
                      {section.title} {index + 1}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {section.fields.map(field =>
                        renderField(
                          field,
                          section.id,
                          item[field.id],
                          (value) => handleFieldChange(section.id, field.id, value, index),
                          index
                        )
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {section.fields.map(field =>
                  renderField(
                    field,
                    section.id,
                    formData[section.id]?.[field.id],
                    (value) => handleFieldChange(section.id, field.id, value)
                  )
                )}
              </div>
            )}
          </div>
        ))}

        <div className="flex gap-4 pt-6 border-t border-gray-200 sticky bottom-0 bg-white p-4 shadow-lg rounded-lg">
          <button 
            type="button" 
            onClick={handleSave} 
            disabled={saving} 
            className="btn btn-primary flex-1"
          >
            <SafeIcon icon={FiSave} className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save Form'}
          </button>
          <button
            type="button"
            onClick={handleGeneratePDF}
            className="btn btn-secondary flex-1"
            title="Generate PDF certificate"
          >
            <SafeIcon icon={FiFileText} className="w-4 h-4" />
            Generate PDF
          </button>
        </div>
      </form>
    </div>
  );
};

export default DomesticGasSafetyForm;
